/**
 * @module features/visual/useVisual
 *
 * React Query hooks for the Visual checkpoint review API (`/visual/*`).
 */
import { useInfiniteQuery, type InfiniteData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  VisualCheckpointDetail,
  VisualCheckpointDetailResponse,
  VisualCheckpointListResponse,
  VisualOutcomeCounts,
  VisualSummaryResponse,
} from '@chatsdet/types';
import { API_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';
import { toApiError } from '../../lib/api-error';
import {
  visualScopeToQuery,
  visualSearchToQuery,
  type VisualScope,
  type VisualSearch,
} from './visualSearch';

const PAGE_SIZE = 50;
const json = { 'content-type': 'application/json' };

async function fetchCheckpointPage(params: URLSearchParams): Promise<VisualCheckpointListResponse['data']> {
  const res = await fetch(`${API_URL}/visual/checkpoints?${params}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, 'Failed to load visual checkpoints');
  return ((await res.json()) as VisualCheckpointListResponse).data;
}

export function useVisualCheckpoints(search: VisualSearch) {
  return useInfiniteQuery<VisualCheckpointListResponse['data'], Error, InfiniteData<VisualCheckpointListResponse['data'], string | undefined>, unknown[], string | undefined>({
    queryKey: ['visual', 'checkpoints', search],
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    queryFn: async ({ pageParam }) => {
      const params = visualSearchToQuery(search);
      params.set('limit', String(PAGE_SIZE));
      if (pageParam) params.set('cursor', pageParam);
      return fetchCheckpointPage(params);
    },
  });
}

/** True once the org has any checkpoint at all, whatever the page filters; one row is enough to tell. Only asked once the list came back empty. */
export function useHasVisualCheckpoints(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['visual', 'has-checkpoints'],
    queryFn: async (): Promise<boolean> =>
      (await fetchCheckpointPage(new URLSearchParams({ status: 'all', limit: '1' }))).items.length > 0,
  });
}

/** Polls only while the compare is pending (a revert re-queues it and nulls the outcome). */
export function useVisualCheckpoint(captureId: string) {
  return useQuery({
    queryKey: ['visual', 'checkpoint', captureId],
    refetchInterval: (query) => (query.state.data && query.state.data.outcome === null ? 3_000 : false),
    queryFn: async (): Promise<VisualCheckpointDetail> => {
      const res = await fetch(`${API_URL}/visual/checkpoints/${captureId}`, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to load visual checkpoint');
      return ((await res.json()) as VisualCheckpointDetailResponse).data;
    },
  });
}

/** Counts for the run-page badge; off unless the org has sandboxV2. */
export function useVisualSummary(scope: VisualScope | null) {
  const { user } = useAuth();
  const enabled = Boolean(user?.features?.sandboxV2) && scope !== null;
  return useQuery({
    queryKey: ['visual', 'summary', scope],
    enabled,
    staleTime: 15_000,
    queryFn: async (): Promise<VisualOutcomeCounts> => {
      const res = await fetch(`${API_URL}/visual/summary?${visualScopeToQuery(scope!)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load visual summary');
      return ((await res.json()) as VisualSummaryResponse).data;
    },
  });
}

function useInvalidateVisual() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['visual'] });
}

export function useMarkVisualRegression(captureId: string) {
  const invalidate = useInvalidateVisual();
  return useMutation({
    mutationFn: async (marked: boolean) => {
      const res = await fetch(`${API_URL}/visual/checkpoints/${captureId}/regression`, {
        method: 'POST',
        credentials: 'include',
        headers: json,
        body: JSON.stringify({ marked }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update regression mark');
    },
    onSuccess: invalidate,
  });
}

export function useRevertVisualMask(captureId: string) {
  const invalidate = useInvalidateVisual();
  return useMutation({
    mutationFn: async (maskId: string) => {
      const res = await fetch(`${API_URL}/visual/checkpoints/${captureId}/mask/${maskId}/revert`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to revert mask');
    },
    onSuccess: invalidate,
  });
}
